import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { TimesheetService } from '../../../services/timesheet.service';
import { Subject, takeUntil } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuxiliarService } from '../../../services/auxiliar.service';
import { NGXLogger } from 'ngx-logger';
import { TranslateService } from '@ngx-translate/core';
import _ from 'lodash';
import moment from 'moment';
import { hourAfterValidator } from '../../../utils/validators/hourAfterValidator';
import { Employee } from '../../../models/employee';

@Component({
  selector: 'app-timesheet-manual-drawer',
  standalone: false,
  templateUrl: './timesheet-manual-drawer.component.html',
  styleUrl: './timesheet-manual-drawer.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class TimesheetManualDrawerComponent implements OnInit, OnDestroy {
  @Input() employee: Employee;

  private destroy$: Subject<boolean> = new Subject<boolean>();
  public form: FormGroup;
  public maxTime: Date | undefined = new Date();
  public timesheetRecordOptions: {value: string, name: string}[] = [];
  public today: Date = new Date();

  get f(): { [index: string]: any } {
    return this.form.controls;
  }

  constructor(
    private formBuilder: FormBuilder,
    private auxiliarService: AuxiliarService,
    private logger: NGXLogger,
    private translate: TranslateService,
    private timesheetService: TimesheetService,
  ){}

  ngOnInit(): void {
    // timesheet record options
    this.timesheetRecordOptions = this.auxiliarService.getTimesheetRecordOptions();
    // load form
    this.loadForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private loadForm(): void {
    this.form = this.formBuilder.group({
      comments: ['', []],
      day: [this.today, [Validators.required]],
      employee: [{value: this.employee?.givenname, disabled: true}, []],
      recordBegin: ['', []],
      recordEnd: ['', []],
      type: ['', [Validators.required]],
    },
    {
      validators: Validators.compose([
        this.auxiliarService.atLeastOneRequired(['recordBegin', 'recordEnd'])
      ])
    });

    this.f['recordEnd'].setValidators([hourAfterValidator(this.f['recordBegin'])]);

    this.f['recordBegin'].valueChanges.subscribe(() => {
      this.f['recordEnd'].updateValueAndValidity();
    });

    this.f['day'].valueChanges.subscribe((date: Date) => {
      if(date && moment(date).isSame(moment(), 'date')) {
        this.maxTime = new Date();
        if(moment(this.f['recordBegin'].value).isAfter(moment(this.maxTime), 'minutes')) {
          this.f['recordBegin'].setValue('');
        }
        if(moment(this.f['recordEnd'].value).isAfter(moment(this.maxTime), 'minutes')) {
          this.f['recordEnd'].setValue('');
        }
      } else {
        this.maxTime = undefined;
      }
    });
  }

  public todaySelected(): boolean {
    return this.f['day'].value && moment(this.f['day'].value).isSame(moment(), 'date');
  }

  public save(): void {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      const formData = _.cloneDeep(this.form.getRawValue());
      _.set(formData, 'day', moment(formData.day).format('YYYY-MM-DD'));
      _.set(formData, 'type', _.get(formData, 'type.value'));
      if(!formData.recordBegin) {
        delete formData.recordBegin;
      } else {
        _.set(formData, 'recordBegin', moment(formData.recordBegin).format('HH:mm'));
      }
      if(!formData.recordEnd) {
        delete formData.recordEnd;
      } else {
        _.set(formData, 'recordEnd', moment(formData.recordEnd).format('HH:mm'));
      }
      delete formData.employee;
      this.timesheetService.createTimesheetRecordManual(formData, this.employee).pipe(
          takeUntil(this.destroy$)
        ).subscribe({
          next: (res: any) => {
            this.logger.debug('[timesheetService] createTimesheetRecordManual res: ', res);
            this.auxiliarService.toast(this.translate.instant('success.timesheet-create'), 'success');
            // check if it is necessary to update user last timesheet in header
            if(moment().isSame(moment(formData.day), 'date')) {
              this.timesheetService.updateUserLastTimesheet();
            }
            this.auxiliarService.closeDrawer('right', _.get(res, 'body'));
          },
          error: (err: any) => {
            this.logger.error('[timesheetService] createTimesheetRecordManual err:', err);
            this.auxiliarService.toast(this.translate.instant('error.timesheet-create'), 'error');
          }
        });
    }
  }

  public cancel(): void {
    this.auxiliarService.closeDrawer('right');
  }
}
